/**
 * What a finished run produced, and the one thing to do next.
 *
 * The success state used to be the word "Tamamlandı" and a button reading
 * "Çıktıları Görüntüle" - which appeared even under the sentence "Bu çalışma
 * geometri üretmedi", offering to show outputs that do not exist.
 *
 * Everything here reads `model_state`, which the server derives from artifacts
 * it validated. That is the only thing allowed to license "3D modeliniz
 * hazır": a sparse run succeeds perfectly well and contains no surface, and
 * calling that a finished model is the most misleading sentence this screen
 * could print.
 */

import { Link } from "react-router-dom";

import type { ReconstructionJob } from "@/api/types";
import { Callout } from "@/components/ui";
import { paths } from "@/app/paths";
import { ElapsedReading } from "./RunProgress";
import { outcomeOf } from "./reconstructionState";

export function RunResult({
  job,
  projectId,
  onInspect,
}: {
  job: ReconstructionJob;
  projectId: string;
  /** Scroll to the point cloud already on this screen. */
  onInspect: (() => void) | null;
}) {
  const outcome = outcomeOf(job.model_state);
  const hasOutputs = job.artifacts.some(
    // The engine log is a diagnostic, not a result. Counting it would put an
    // "outputs" action under a run that produced no geometry at all.
    (artifact) => artifact.artifact_kind !== "engine_log",
  );

  return (
    <Callout tone={outcome.tone} title={outcome.headline}>
      <div className="stack-tight">
        <p className="muted">{outcome.detail}</p>
        {/*
          How long it took, frozen at `finished_at`. A finished run whose
          reading kept climbing would be counting time nothing is spending.
        */}
        <ElapsedReading job={job} />

        <div className="row">
          {/*
            The model action exists only where a model does. `has_model` is
            the predicate, never the job status and never a guess from the
            artifact list.
          */}
          {outcome.key === "model" && (
            <Link className="btn btn--primary" to={paths.projectModel(projectId)}>
              3D modeli görüntüle
            </Link>
          )}
          {outcome.key !== "model" && outcome.key !== "none" && outcome.key !== "unknown" && onInspect && (
            <button type="button" className="btn btn--primary" onClick={onInspect}>
              Sonucu incele
            </button>
          )}
          {/*
            Secondary where there is a model to look at first, and absent
            entirely when the run produced nothing worth opening.
          */}
          {hasOutputs && outcome.key !== "none" && outcome.key !== "unknown" && (
            <Link className="btn btn--secondary" to={paths.projectFiles(projectId)}>
              Çıktıları görüntüle
            </Link>
          )}
        </div>
      </div>
    </Callout>
  );
}
